import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Button from '../../../../../../shared/components/general/Button';
import PageError from '../../../../../../shared/components/general/PageError';
import PmsLookupContainer, { PmsLookupState } from '../../../../../../shared/hooks/PmsLookupContainer';
import { silver_two, dark_grey_blue, color_shades_darker, color_colors_ocean } from '../../../../../../shared/constants/colors';
import { CopyIdButton, EmptyState, TextLinkButton } from '../../../../../../shared/scenes/organizationSettings/components/MedicalIntegrationConfigSettings';
import { PmsOfficeModel, PmsProcedureTypeModel, PmsSlotModel } from '../../../../../../shared/models/organizations/PmsLookupModels';
import { MenuSecondaryText, SearchPicker } from './SearchPicker';

export interface LookupColumn<T> {
    header: string;
    value: (row: T) => string | undefined;
    copyable?: boolean;
    // Custom cell content, e.g. an action button; takes precedence over value
    render?: (row: T) => React.ReactNode;
}

interface LookupTableProps<T> {
    lookup: PmsLookupState<T[]>;
    columns: LookupColumn<T>[];
    emptyText?: string;
    onRowClick?: (row: T) => void;
    selectedRowKey?: string;
    rowKey?: (row: T) => string;
}

// Result table in the Location Manager style; handles loading, errors and empty results
export function LookupTable<T>({ lookup, columns, emptyText = 'No results found.', onRowClick, selectedRowKey, rowKey }: LookupTableProps<T>) {
    if (lookup.isLoading) {
        return <EmptyState>Loading...</EmptyState>;
    }
    if (lookup.errors.length > 0) {
        return <PageError errors={lookup.errors} />;
    }
    if (!lookup.data) {
        return null;
    }
    if (lookup.data.length === 0) {
        return <EmptyState>{emptyText}</EmptyState>;
    }

    return (
        <>
            <ResultCount>{lookup.data.length} result{lookup.data.length === 1 ? '' : 's'}</ResultCount>
            <TableContainer>
                <Table>
                    <thead>
                        <tr>
                            {columns.map(column => <Th key={column.header}>{column.header}</Th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {lookup.data.map((row, index) => (
                            <Tr
                                key={rowKey ? rowKey(row) : index}
                                clickable={!!onRowClick}
                                selected={!!rowKey && rowKey(row) === selectedRowKey}
                                onClick={() => onRowClick?.(row)}
                            >
                                {columns.map(column => {
                                    const value = column.value(row) ?? '';
                                    return (
                                        <Td key={column.header}>
                                            {column.render ? column.render(row) : column.copyable && value ? (
                                                <IdCell>
                                                    <MonoText title={value}>{value}</MonoText>
                                                    <CopyIdButton value={value} />
                                                </IdCell>
                                            ) : value}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        ))}
                    </tbody>
                </Table>
            </TableContainer>
        </>
    );
}

const formatDateTime = (value?: string) => value ? new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '';

export const personName = (person: { firstName?: string; lastName?: string }) => [person.firstName, person.lastName].filter(Boolean).join(' ');

const toIsoDate = (date: Date) => date.toISOString().split('T')[0];
const daysFromToday = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return toIsoDate(date);
};

// Loads a lookup the first time its tab is shown (and again after its inputs reset it)
export const useAutoLoad = (lookup: PmsLookupState<any>, load: () => void, ready: boolean = true) => {
    useEffect(() => {
        if (ready && !lookup.data && !lookup.isLoading && lookup.errors.length === 0) {
            load();
        }
    }, [ready, lookup.data, lookup.isLoading, lookup.errors.length]);
};

export const RefreshButton: React.FC<{ isLoading: boolean; onClick: () => void }> = ({ isLoading, onClick }) => (
    <ToolbarButton themes={['white-small']} text={isLoading ? 'Loading...' : 'Refresh'} onClick={onClick} disabled={isLoading} />
);

interface ProcedureTypesLookupProps {
    // Renders the loaded types instead of the read-only table (the Spark PMS Manager uses the assistant editor)
    renderTypes?: (types: PmsProcedureTypeModel[]) => React.ReactNode;
}

export const ProcedureTypesLookup: React.FC<ProcedureTypesLookupProps> = ({ renderTypes }) => {
    const pms = PmsLookupContainer.useContainer();
    const onlyBookable = pms.procedureTypesOnlyBookable;
    useAutoLoad(pms.procedureTypes, () => pms.loadProcedureTypes(), !!pms.officeId);

    if (!pms.officeId) {
        return <EmptyState>Select an office to see its procedure types.</EmptyState>;
    }

    return (
        <>
            <Toolbar>
                <CheckboxLabel>
                    <input
                        type="checkbox"
                        checked={onlyBookable}
                        onChange={(e) => pms.loadProcedureTypes(e.target.checked)}
                    />
                    Only bookable online
                </CheckboxLabel>
                <RefreshButton isLoading={pms.procedureTypes.isLoading} onClick={() => pms.loadProcedureTypes(onlyBookable, true)} />
            </Toolbar>
            {renderTypes && pms.procedureTypes.data?.length > 0 && !pms.procedureTypes.isLoading ? renderTypes(pms.procedureTypes.data) : <LookupTable
                lookup={pms.procedureTypes}
                columns={[
                    { header: 'Procedure Type ID', value: row => row.id, copyable: true },
                    { header: 'Name', value: row => row.name },
                    { header: 'Duration', value: row => row.durationMinutes ? `${row.durationMinutes} min` : '' },
                    { header: 'Bookable', value: row => row.isBookable === undefined ? '' : (row.isBookable ? 'Yes' : 'No') }
                ]}
            />}
        </>
    );
};

interface AvailableSlotsLookupProps {
    isCloud9: boolean;
}

// voicify-integrations rejects slot searches longer than this
const MAX_SLOT_RANGE_DAYS = 60;

const daysBetween = (start: string, end: string) => Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86400000);

export const AvailableSlotsLookup: React.FC<AvailableSlotsLookupProps> = ({ isCloud9 }) => {
    const pms = PmsLookupContainer.useContainer();
    const [procedureTypeId, setProcedureTypeId] = useState('');
    const [providerId, setProviderId] = useState('');
    const [scheduleViewId, setScheduleViewId] = useState('');
    const [startDate, setStartDate] = useState(daysFromToday(0));
    const [endDate, setEndDate] = useState(daysFromToday(7));

    // The pickers need these lists, so pull them in rather than making the user visit other tabs first
    useAutoLoad(pms.procedureTypes, () => pms.loadProcedureTypes(false), !!pms.officeId);
    useAutoLoad(pms.providers, () => pms.loadProviders(), !isCloud9);
    useAutoLoad(pms.cloud9ScheduleViews, () => pms.loadCloud9ScheduleViews(), isCloud9);

    if (!pms.officeId) {
        return <EmptyState>Select an office to search for open appointment slots.</EmptyState>;
    }

    // Cloud 9 slots aren't tied to a provider, so its search has no provider filter
    const providerOptions = pms.providers.data ?? [];
    const officeScheduleViews = (pms.cloud9ScheduleViews.data ?? []).filter(view => !view.locationGuid || view.locationGuid === pms.officeId);

    const rangeDays = startDate && endDate ? daysBetween(startDate, endDate) : -1;
    const rangeError = !startDate || !endDate
        ? 'Pick a start and end date.'
        : rangeDays < 0
            ? 'The end date must be on or after the start date.'
            : rangeDays > MAX_SLOT_RANGE_DAYS
                ? `The date range can be at most ${MAX_SLOT_RANGE_DAYS} days.`
                : null;
    const canSearch = !!procedureTypeId && !rangeError;

    const providerLabel = (id?: string) => {
        const provider = providerOptions.find(option => option.id === id);
        const name = provider ? personName(provider) : '';
        return name ? `${name} (${id})` : id;
    };

    return (
        <>
            <FieldRow>
                <LabeledField>
                    <FieldLabel>Procedure type <Required>required</Required></FieldLabel>
                    <ToolbarSelect value={procedureTypeId} onChange={(e) => setProcedureTypeId(e.target.value)}>
                        <option value="">{pms.procedureTypes.isLoading ? 'Loading...' : 'Select procedure type'}</option>
                        {(pms.procedureTypes.data ?? []).map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                    </ToolbarSelect>
                </LabeledField>
                <LabeledField>
                    <FieldLabel>From <Required>required</Required></FieldLabel>
                    <ToolbarInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </LabeledField>
                <LabeledField>
                    <FieldLabel>To <Required>required</Required></FieldLabel>
                    <ToolbarInput type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
                </LabeledField>
                {isCloud9 ? (
                    <LabeledField>
                        <FieldLabel>Schedule view <Optional>optional</Optional></FieldLabel>
                        <ToolbarSelect value={scheduleViewId} onChange={(e) => setScheduleViewId(e.target.value)}>
                            <option value="">All schedule views at this office</option>
                            {officeScheduleViews.map(view => <option key={view.guid} value={view.guid}>{view.description}</option>)}
                        </ToolbarSelect>
                    </LabeledField>
                ) : (
                    <LabeledField>
                        <FieldLabel>Provider <Optional>optional</Optional></FieldLabel>
                        <ToolbarSelect value={providerId} onChange={(e) => setProviderId(e.target.value)}>
                            <option value="">Any provider</option>
                            {providerOptions.map(provider => <option key={provider.id} value={provider.id}>{personName(provider) || provider.id}</option>)}
                        </ToolbarSelect>
                    </LabeledField>
                )}
                <ToolbarButton
                    themes={['primary-small']}
                    text={pms.slots.isLoading ? 'Searching...' : 'Find Slots'}
                    onClick={() => pms.loadSlots({
                        procedureTypeId,
                        startDate,
                        endDate,
                        providerId: !isCloud9 && providerId ? providerId : undefined,
                        scheduleViewId: isCloud9 && scheduleViewId ? scheduleViewId : undefined
                    })}
                    disabled={!canSearch || pms.slots.isLoading}
                />
            </FieldRow>
            {rangeError && <FieldHint error>{rangeError}</FieldHint>}
            <PageError errors={pms.cloud9ScheduleViews.errors} />
            {!pms.slots.data && !pms.slots.isLoading && pms.slots.errors.length === 0 ? (
                <EmptyState>Pick a procedure type and date range, then find slots.</EmptyState>
            ) : (
                <LookupTable
                    lookup={pms.slots}
                    emptyText="No open slots in this date range."
                    columns={[
                        { header: 'Start', value: row => formatDateTime(row.start) },
                        { header: 'End', value: row => formatDateTime(row.end) },
                        ...(isCloud9 ? [] : [{ header: 'Provider', value: (row: PmsSlotModel) => providerLabel(row.providerId) }]),
                        { header: isCloud9 ? 'Chair' : 'Operatory', value: row => row.operatoryId }
                    ]}
                />
            )}
        </>
    );
};

type PatientField = 'firstName' | 'lastName' | 'birthDate';

interface PatientSearchRules {
    required: PatientField[];
    // Some systems need at least one of these rather than a fixed set
    atLeastOneOf?: PatientField[];
    allowsPhone: boolean;
    // Whether the PMS can fetch one patient by ID (Cloud 9 can't)
    allowsPatientId: boolean;
    hint: string;
}

// What each PMS's patient search call needs (see the PmsLookup services in voicify-integrations)
const PATIENT_SEARCH_RULES: Record<string, PatientSearchRules> = {
    'denticon': {
        required: ['firstName', 'lastName', 'birthDate'],
        allowsPhone: true,
        allowsPatientId: true,
        hint: "Denticon's existing-patient check needs first name, last name and date of birth for the selected office. Or search by phone number or patient ID on its own."
    },
    'cloud-9': {
        required: ['lastName'],
        allowsPhone: false,
        allowsPatientId: false,
        hint: 'Cloud 9 searches by last name. First name narrows the search and date of birth filters the results.'
    },
    'dentrix-ascend': {
        required: [],
        atLeastOneOf: ['firstName', 'lastName', 'birthDate'],
        allowsPhone: false,
        allowsPatientId: true,
        hint: 'Dentrix Ascend matches exactly on any of first name, last name and date of birth. Or look up a patient ID on its own.'
    },
    'open-dental': {
        required: [],
        atLeastOneOf: ['firstName', 'lastName'],
        allowsPhone: true,
        allowsPatientId: true,
        hint: 'Open Dental needs a first or last name (date of birth optional). Or search by phone number or patient ID on its own.'
    }
};

const PATIENT_FIELD_LABELS: Record<PatientField, string> = {
    firstName: 'First name',
    lastName: 'Last name',
    birthDate: 'Date of birth'
};

interface PatientsLookupProps {
    pmsName: string;
}

export const PatientsLookup: React.FC<PatientsLookupProps> = ({ pmsName }) => {
    const pms = PmsLookupContainer.useContainer();
    const [values, setValues] = useState<Record<PatientField, string>>({ firstName: '', lastName: '', birthDate: '' });
    const [phone, setPhone] = useState('');
    const [patientId, setPatientId] = useState('');

    const rules = PATIENT_SEARCH_RULES[pmsName] ?? PATIENT_SEARCH_RULES['open-dental'];
    const filled = (field: PatientField) => !!values[field].trim();
    const hasNameSearch = (Object.keys(values) as PatientField[]).some(filled);
    const hasPhone = rules.allowsPhone && !!phone.trim();
    const isPhoneSearch = hasPhone && !hasNameSearch;
    // A patient ID is its own search: one patient, straight to the details card
    const isIdSearch = rules.allowsPatientId && !hasNameSearch && !hasPhone && !!patientId.trim();

    const nameSearchError = (): string | null => {
        const missing = rules.required.filter(field => !filled(field));
        if (missing.length > 0) {
            return `Add ${missing.map(field => PATIENT_FIELD_LABELS[field].toLowerCase()).join(', ')}.`;
        }
        if (rules.atLeastOneOf && !rules.atLeastOneOf.some(filled)) {
            return `Add ${rules.atLeastOneOf.map(field => PATIENT_FIELD_LABELS[field].toLowerCase()).join(' or ')}.`;
        }
        return null;
    };
    const searchError = isPhoneSearch || isIdSearch || !hasNameSearch ? null : nameSearchError();
    const canSearch = isPhoneSearch || isIdSearch || (hasNameSearch && !searchError);

    const fieldState = (field: PatientField) => rules.required.includes(field)
        ? <Required>required</Required>
        : rules.atLeastOneOf?.includes(field) ? <Optional>one of</Optional> : <Optional>optional</Optional>;

    const search = () => {
        if (!canSearch) return;
        pms.clearPatientDetail();
        if (isIdSearch) {
            pms.clearPatients();
            pms.loadPatient(patientId.trim());
            return;
        }
        // Name and phone searches are separate PMS calls, so only send the one being used
        pms.searchPatients(isPhoneSearch
            ? { phone: phone.trim() }
            : {
                firstName: values.firstName.trim() || undefined,
                lastName: values.lastName.trim() || undefined,
                birthDate: values.birthDate || undefined
            });
    };

    const detail = pms.patientDetail;

    return (
        <>
            <FieldHint>{rules.hint}</FieldHint>
            <FieldRow as="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); search(); }}>
                {(['firstName', 'lastName'] as PatientField[]).map(field => (
                    <LabeledField key={field}>
                        <FieldLabel>{PATIENT_FIELD_LABELS[field]} {fieldState(field)}</FieldLabel>
                        <ToolbarInput value={values[field]} onChange={(e) => setValues({ ...values, [field]: e.target.value })} />
                    </LabeledField>
                ))}
                <LabeledField>
                    <FieldLabel>Date of birth {fieldState('birthDate')}</FieldLabel>
                    <ToolbarInput type="date" value={values.birthDate} onChange={(e) => setValues({ ...values, birthDate: e.target.value })} />
                </LabeledField>
                {rules.allowsPhone && (
                    <>
                        <OrText>or</OrText>
                        <LabeledField>
                            <FieldLabel>Phone number</FieldLabel>
                            <ToolbarInput
                                type="tel"
                                value={phone}
                                disabled={hasNameSearch || !!patientId.trim()}
                                title={hasNameSearch || patientId.trim() ? 'Clear the other fields to search by phone' : undefined}
                                onChange={(e) => setPhone(e.target.value)}
                            />
                        </LabeledField>
                    </>
                )}
                {rules.allowsPatientId && (
                    <>
                        <OrText>or</OrText>
                        <LabeledField>
                            <FieldLabel>Patient ID</FieldLabel>
                            <ToolbarInput
                                value={patientId}
                                disabled={hasNameSearch || hasPhone}
                                title={hasNameSearch || hasPhone ? 'Clear the other fields to search by patient ID' : undefined}
                                onChange={(e) => setPatientId(e.target.value)}
                            />
                        </LabeledField>
                    </>
                )}
                <ToolbarButton
                    themes={['primary-small']}
                    type="submit"
                    text={pms.patients.isLoading || (isIdSearch && detail.isLoading) ? 'Searching...' : 'Search'}
                    disabled={!canSearch || pms.patients.isLoading || detail.isLoading}
                />
            </FieldRow>
            {searchError && <FieldHint error>{searchError}</FieldHint>}
            {!pms.patients.data && !pms.patients.isLoading && pms.patients.errors.length === 0 ? (
                !detail.data && !detail.isLoading && detail.errors.length === 0 && <EmptyState>Fill in the search fields, then search.</EmptyState>
            ) : (
                <LookupTable
                    lookup={pms.patients}
                    emptyText="No matching patients."
                    rowKey={row => row.id}
                    selectedRowKey={detail.data?.id}
                    onRowClick={(patient) => patient.id !== detail.data?.id && pms.loadPatient(patient.id)}
                    columns={[
                        { header: 'Patient ID', value: row => row.id, copyable: true },
                        { header: 'Name', value: row => personName(row) },
                        { header: 'Date of Birth', value: row => row.birthDate?.split('T')[0] },
                        { header: 'Office', value: row => row.officeId },
                        // Rows open on click too; this button is the keyboard-reachable way in
                        {
                            header: 'Details',
                            value: () => '',
                            render: row => (
                                <TextLinkButton type="button" aria-label={`View details for patient ${row.id}`} onClick={(e) => { e.stopPropagation(); if (row.id !== detail.data?.id) pms.loadPatient(row.id); }}>
                                    View
                                </TextLinkButton>
                            )
                        }
                    ]}
                />
            )}
            {detail.isLoading && <DetailLoading>Loading patient details...</DetailLoading>}
            <PageError errors={detail.errors} />
            {detail.data && (
                <DetailCard>
                    <DetailTitle>{personName(detail.data)}</DetailTitle>
                    <DetailGrid>
                        <span>Patient ID</span><span>{detail.data.id}</span>
                        {detail.data.birthDate && <><span>Date of birth</span><span>{detail.data.birthDate.split('T')[0]}</span></>}
                        {detail.data.phone && <><span>Phone</span><span>{detail.data.phone}</span></>}
                        {detail.data.email && <><span>Email</span><span>{detail.data.email}</span></>}
                        {detail.data.officeId && <><span>Office</span><span>{detail.data.officeId}</span></>}
                    </DetailGrid>
                </DetailCard>
            )}
        </>
    );
};

interface OfficePickerProps {
    offices: PmsOfficeModel[];
    selectedOfficeId: string;
    isLoading: boolean;
    onSelect: (officeId: string) => void;
}

// Short numeric IDs help match Postman results; GUIDs only add noise
const officeLabel = (office: PmsOfficeModel) => office.id.length <= 12 ? `${office.name} (${office.id})` : office.name;

// Matches on name or ID since practices can have dozens of offices
export const OfficePicker: React.FC<OfficePickerProps> = ({ offices, selectedOfficeId, isLoading, onSelect }) => (
    <SearchPicker
        items={offices}
        selectedId={selectedOfficeId}
        getId={office => office.id}
        selectedLabel={officeLabel}
        searchText={office => [office.name, office.id]}
        renderItem={office => <><span>{office.name}</span><MenuSecondaryText>{office.id}</MenuSecondaryText></>}
        onSelect={office => onSelect(office.id)}
        placeholder={isLoading ? 'Loading offices...' : offices.length ? 'Search offices by name or ID' : 'No offices'}
        ariaLabel="Office"
        noun="offices"
        disabled={isLoading}
    />
);

export const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 16px;
`;

export const ToolbarSelect = styled.select`
    padding: 8px 36px 8px 16px;
    border: 1px solid ${silver_two};
    border-radius: 20px;
    font-family: Muli;
    font-size: 13px;
    outline: none;
    background-color: #ffffff;
    cursor: pointer;
    min-width: 160px;
    max-width: 280px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23718096' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-size: 13px;
    transition: all 0.2s ease-in-out;

    &:hover {
        border-color: #cbd5e0;
    }

    &:focus {
        border-color: #007dbb;
        box-shadow: 0 0 0 3px rgba(0, 125, 187, 0.15);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    @media (max-width: 576px) {
        width: 100%;
        max-width: none;
    }
`;

const ToolbarInput = styled.input`
    height: 34px;
    padding: 8px 16px;
    border: 1px solid ${silver_two};
    border-radius: 20px;
    font-family: Muli;
    font-size: 13px;
    outline: none;
    box-sizing: border-box;
    transition: all 0.2s ease-in-out;
    width: 160px;

    &:hover {
        border-color: #cbd5e0;
    }

    &:focus {
        border-color: #007dbb;
        box-shadow: 0 0 0 3px rgba(0, 125, 187, 0.15);
    }

    @media (max-width: 576px) {
        width: 100%;
    }
`;

const ToolbarButton = styled(Button)`
    height: 34px;
    min-height: 34px;
    padding: 0 16px;
    margin: 0;
    font-size: 13px;
`;

const OrText = styled.span`
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darker};
`;

const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: Muli;
    font-size: 13px;
    color: ${dark_grey_blue};
    cursor: pointer;
`;

export const HelpText = styled.span`
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darker};
    flex: 1;
`;

const ResultCount = styled.div`
    font-family: Muli;
    font-size: 12px;
    color: ${color_shades_darker};
    margin-bottom: 8px;
`;

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    max-height: 420px;
    overflow-y: auto;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    background-color: #ffffff;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-family: Muli;
`;

const Th = styled.th`
    position: sticky;
    top: 0;
    background-color: #edf2f7;
    border-bottom: 1px solid ${silver_two};
    color: #4a5568;
    font-size: 13px;
    font-weight: 700;
    padding: 12px 16px;
    text-align: left;
    white-space: nowrap;
`;

const Tr = styled.tr<{ clickable: boolean; selected: boolean }>`
    cursor: ${props => props.clickable ? 'pointer' : 'default'};
    background-color: ${props => props.selected ? 'rgba(46, 125, 206, 0.08)' : 'transparent'};
    transition: background-color 0.15s ease-in-out;

    &:hover {
        background-color: ${props => props.selected ? 'rgba(46, 125, 206, 0.08)' : '#f8fafc'};
    }

    &:last-of-type td {
        border-bottom: none;
    }
`;

const Td = styled.td`
    padding: 12px 16px;
    border-bottom: 1px solid ${silver_two};
    font-size: 13px;
    color: #2d3748;
    vertical-align: middle;
`;

const IdCell = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const MonoText = styled.span`
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DetailLoading = styled.div`
    margin-top: 16px;
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darker};
`;

const FieldRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
`;

const LabeledField = styled.label`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const FieldLabel = styled.span`
    font-family: Muli;
    font-size: 12px;
    font-weight: 600;
    color: ${dark_grey_blue};
    padding-left: 4px;
`;

const Required = styled.span`
    font-weight: 400;
    color: ${color_colors_ocean};
`;

const Optional = styled.span`
    font-weight: 400;
    color: ${color_shades_darker};
`;

const FieldHint = styled.div<{ error?: boolean }>`
    font-family: Muli;
    font-size: 12px;
    color: ${props => props.error ? '#c53030' : color_shades_darker};
    margin-bottom: 12px;
`;

const DetailCard = styled.div`
    margin-top: 16px;
    padding: 16px;
    border: 1px solid ${color_colors_ocean};
    border-radius: 8px;
    background-color: rgba(46, 125, 206, 0.05);
`;

const DetailTitle = styled.div`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    color: ${dark_grey_blue};
    margin-bottom: 8px;
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 4px 16px;
    font-family: Muli;
    font-size: 13px;
    color: #2d3748;

    span:nth-of-type(odd) {
        color: ${color_shades_darker};
    }
`;
